import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Box,
  Container,
  Toolbar,
  Typography,
  Button,
  Stack,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Fab,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";

import { normalizeHttpsUrl, useDataStore } from "../dataStore";
import { toMediaUrl } from "../api";
import { brandThemeCssVars } from "../themeTokens";
import { useClientPortal } from "../clientPortal/ClientPortalContext";
import OptimizedConfigImage from "../components/OptimizedConfigImage";

const HEADER_H_XS = 64;
const HEADER_H_SM = 72;
const ORDER_PATH = "/skipline";
const MORE_BUTTON_ID = "app-shell-more-button";
const MORE_MENU_ID = "app-shell-more-menu";
const LANGUAGE_BUTTON_ID = "app-shell-language-button";
const LANGUAGE_MENU_ID = "app-shell-language-menu";
const ACCOUNT_BUTTON_ID = "app-shell-account-button";
const ACCOUNT_MENU_ID = "app-shell-account-menu";
const MOBILE_NAV_ID = "app-shell-mobile-navigation";
const orderTargetProps = {
  target: "_blank",
  rel: "noopener noreferrer",
};

function safeExternalUrl(value) {
  const candidate = String(value || "").trim();
  if (!candidate) return "";

  try {
    const url = new URL(candidate);
    return url.protocol === "https:" ? candidate : "";
  } catch {
    return "";
  }
}

export default function AppShell({ children }) {
  const {
    logoUrl,
    brandName,
    brandTagline,
    brandTheme,
    publicConfigReady,
    publicContentError,
    retryPublicContent,
    languages,
    selectedLanguageIref,
    setSelectedLanguageIref,
    languageCode,
    websiteContent,
    appStoreUrl,
    playStoreUrl,
    t,
  } = useDataStore();
  const {
    enabled: clientPortalEnabled,
    isAuthenticated: clientPortalAuthenticated,
    authenticatedCheckoutEnabled,
    profile: clientProfile,
    logout: logoutClient,
  } = useClientPortal();
  const keepClientOrderInTab =
    clientPortalAuthenticated && authenticatedCheckoutEnabled;
  const showTopbarOrderButton =
    websiteContent?.topbar?.orderEnabled !== false;
  const c = brandTheme;
  const displayName = brandName || (publicConfigReady ? "Restaurant" : "");
  const brandLabel = displayName || "Restaurant";
  const displayInitial = displayName ? displayName.charAt(0).toUpperCase() : "";
  const logoAlt = `${displayName || "Restaurant"} logo`;

  const { pathname } = useLocation();
  const isAppDownload = pathname.replace(/\/+$/, "") === "/app";
  const navigate = useNavigate();

  const theme = useTheme();
  const isCompactNav = useMediaQuery(theme.breakpoints.down("lg"), { noSsr: true });

  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const moreOpen = Boolean(moreAnchorEl);
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const languageOpen = Boolean(languageAnchorEl);
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const accountOpen = Boolean(accountAnchorEl);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showTopAction, setShowTopAction] = useState(false);
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine !== false
  );
  const hasLanguages = Array.isArray(languages) && languages.length > 0;
  const hasAppCapability = Boolean(
    normalizeHttpsUrl(appStoreUrl) || normalizeHttpsUrl(playStoreUrl)
  );

  const mainNav = useMemo(
    () => [
      { label: t("nav.home"), to: "/", icon: <HomeRoundedIcon fontSize="small" /> },
      { label: t("nav.products"), to: "/products", icon: <RestaurantMenuRoundedIcon fontSize="small" /> },
      { label: t("nav.locations"), to: "/locations", icon: <StorefrontRoundedIcon fontSize="small" /> },
    ],
    [t]
  );

  const moreNav = useMemo(
    () => [
      { label: t("nav.invoice"), to: "/invoice", icon: <ReceiptLongRoundedIcon fontSize="small" /> },
      { label: t("nav.feedback"), to: "/feedback", icon: <RateReviewRoundedIcon fontSize="small" /> },
      ...(hasAppCapability
        ? [
            {
              label: t("nav.app"),
              to: "/app",
              icon: <PhoneIphoneRoundedIcon fontSize="small" />,
            },
          ]
        : []),
    ],
    [hasAppCapability, t]
  );

  const allNav = useMemo(() => [...mainNav, ...moreNav], [mainNav, moreNav]);
  const footerNav = useMemo(
    () => [...mainNav, ...moreNav.filter((item) => item.to === "/app")],
    [mainNav, moreNav]
  );
  const footer =
    websiteContent?.footer && typeof websiteContent.footer === "object"
      ? websiteContent.footer
      : {};
  const footerText =
    typeof footer.text === "string" ? footer.text.trim() : "";
  const socialLinks = [
    {
      label: "Instagram",
      href: safeExternalUrl(footer.instagramUrl),
      icon: <InstagramIcon />,
    },
    {
      label: "Facebook",
      href: safeExternalUrl(footer.facebookUrl),
      icon: <FacebookRoundedIcon />,
    },
  ].filter((item) => item.href);

  const isActive = (to) => {
    return pathname === to || (to !== "/" && pathname.startsWith(to + "/"));
  };

  const handleClientLogout = async () => {
    setDrawerOpen(false);
    setMoreAnchorEl(null);
    setAccountAnchorEl(null);
    const revocation = logoutClient();
    navigate("/");
    await revocation;
  };

  useEffect(() => {
    const onScroll = () => {
      setShowTopAction(window.scrollY > 520);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const markOnline = () => setOnline(true);
    const markOffline = () => setOnline(false);
    window.addEventListener("online", markOnline);
    window.addEventListener("offline", markOffline);
    return () => {
      window.removeEventListener("online", markOnline);
      window.removeEventListener("offline", markOffline);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  useEffect(() => {
    setAccountAnchorEl(null);
  }, [pathname, clientPortalEnabled, clientPortalAuthenticated, isCompactNav]);

  const scrollToTop = () => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    )?.matches;
    window.scrollTo({
      top: 0,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  const closeLanguageMenu = () => setLanguageAnchorEl(null);

  const chooseLanguage = (iref) => {
    setSelectedLanguageIref(iref);
    closeLanguageMenu();
  };

  const languageButton = hasLanguages ? (
    <Button
      id={LANGUAGE_BUTTON_ID}
      onClick={(event) => setLanguageAnchorEl(event.currentTarget)}
      aria-label={t("language.change")}
      aria-controls={LANGUAGE_MENU_ID}
      aria-haspopup="menu"
      aria-expanded={languageOpen}
      sx={{
        borderRadius: "8px",
        minWidth: { xs: 52, sm: 60 },
        px: { xs: 1, sm: 1.25 },
        py: 0.78,
        fontWeight: 950,
        color: c.text,
        bgcolor: "rgba(255,255,255,0.72)",
        border: "1px solid rgba(124,45,18,0.12)",
        textTransform: "none",
        "&:hover": {
          bgcolor: "#ffffff",
        },
      }}
    >
      {languageCode}
    </Button>
  ) : null;

  const navButtonSx = (active) => ({
    borderRadius: "8px",
    px: 1.45,
    py: 0.85,
    fontWeight: 850,
    textTransform: "none",
    color: active ? c.onPrimary : c.text,
    bgcolor: active ? c.primary : "transparent",
    border: "1px solid",
    borderColor: active ? "rgba(17,17,17,0.08)" : "transparent",
    boxShadow: active ? "0 10px 24px rgba(247,181,0,0.22)" : "none",
    "&:hover": {
      bgcolor: active ? c.primary : "rgba(255,237,213,0.76)",
    },
  });

  const orderButtonSx = {
    borderRadius: "8px",
    px: { xs: 1.25, sm: 1.5 },
    py: 0.5,
    minWidth: { xs: 64, sm: 76 },
    minHeight: 44,
    fontWeight: 850,
    textTransform: "none",
    color: c.cta,
    bgcolor:
      "color-mix(in srgb, var(--brand-cta) 7%, var(--brand-surface))",
    border: "1px solid",
    borderColor:
      "color-mix(in srgb, var(--brand-cta) 38%, transparent)",
    boxShadow: "none",
    "&:hover": {
      color: c.cta,
      bgcolor:
        "color-mix(in srgb, var(--brand-cta) 12%, var(--brand-surface))",
      borderColor:
        "color-mix(in srgb, var(--brand-cta) 52%, transparent)",
      boxShadow: "none",
    },
    "&:focus-visible": {
      outline: `3px solid ${c.primary}`,
      outlineOffset: 2,
    },
  };

  const accountButtonSx = {
    borderRadius: "8px",
    minHeight: 44,
    px: 1.25,
    color: c.text,
    fontWeight: 700,
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": { bgcolor: "rgba(255,237,213,0.76)" },
    "&:focus-visible": {
      outline: `3px solid ${c.primary}`,
      outlineOffset: 2,
    },
  };

  return (
    <Box
      sx={{
        ...brandThemeCssVars(c),
        "--app-header-height": `${HEADER_H_XS}px`,
        [theme.breakpoints.up("sm")]: {
          "--app-header-height": `${HEADER_H_SM}px`,
        },
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100vw",
        bgcolor: c.background,
        backgroundImage:
          "radial-gradient(circle at 8% 4%, color-mix(in srgb, var(--brand-primary) 10%, transparent) 0, transparent 28%), radial-gradient(circle at 96% 24%, color-mix(in srgb, var(--brand-cta) 7%, transparent) 0, transparent 25%)",
      }}
    >
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "fixed",
          top: 8,
          left: 8,
          zIndex: 1400,
          px: 2,
          py: 1,
          borderRadius: "10px",
          bgcolor: c.dark,
          color: "#fff",
          fontWeight: 850,
          textDecoration: "none",
          transform: "translateY(-160%)",
          transition: "transform 120ms ease",
          "&:focus": {
            transform: "translateY(0)",
          },
        }}
      >
        {t("nav.skipToContent")}
      </Box>

      {/* Sticky top menu */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 0,
          zIndex: 1200,
          color: c.text,
          bgcolor: "rgba(255,250,243,0.86)",
          backdropFilter: "blur(22px)",
          borderBottom: "1px solid rgba(67,20,7,0.10)",
          boxShadow: "0 10px 34px rgba(33,20,12,0.08)",
        }}
      >
        <Toolbar
          sx={{
            minHeight: {
              xs: `${HEADER_H_XS}px`,
              sm: `${HEADER_H_SM}px`,
            },
            gap: 1.5,
            px: { xs: 1.5, sm: 2.5 },
          }}
        >
          {/* Mobile hamburger */}
          {isCompactNav ? (
            <IconButton
              onClick={() => setDrawerOpen(true)}
              edge="start"
              aria-label={t("nav.openMenu")}
              aria-controls={MOBILE_NAV_ID}
              aria-expanded={drawerOpen}
              sx={{
                color: c.accent,
                borderRadius: "8px",
                bgcolor: "rgba(255,237,213,0.72)",
                border: "1px solid rgba(124,45,18,0.10)",
                "&:hover": {
                  bgcolor: "#ffedd5",
                },
              }}
            >
              <MenuRoundedIcon />
            </IconButton>
          ) : null}

          {/* Brand */}
          <Stack
            component={RouterLink}
            to="/"
            direction="row"
            spacing={1.15}
            alignItems="center"
            sx={{
              textDecoration: "none",
              color: "inherit",
              minWidth: 0,
              flexShrink: 1,
            }}
          >
            <Box
              sx={{
                width: { xs: 42, sm: 46 },
                height: { xs: 42, sm: 46 },
                borderRadius: "8px",
                bgcolor: "rgba(255,255,255,0.78)",
                border: "1px solid rgba(124,45,18,0.10)",
                boxShadow: "0 8px 22px rgba(124,45,18,0.10)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {logoUrl ? (
                <OptimizedConfigImage
                  src={logoUrl}
                  alt={logoAlt}
                  style={{
                    maxHeight: 39,
                    maxWidth: 39,
                    width: "auto",
                    display: "block",
                  }}
                />
              ) : (
                displayInitial ? (
                  <Typography sx={{ fontWeight: 950, color: c.accent }}>
                    {displayInitial}
                  </Typography>
                ) : (
                  <RestaurantMenuRoundedIcon
                    aria-hidden="true"
                    sx={{ color: c.accent, fontSize: 23 }}
                  />
                )
              )}
            </Box>

            <Box sx={{ minWidth: 0, display: "block" }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 950,
                  letterSpacing: 0,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: { xs: 145, sm: 230 },
                  fontSize: { xs: 16, sm: 20 },
                  color: c.text,
                  overflowWrap: "anywhere",
                }}
              >
                {brandLabel}
              </Typography>

              {brandTagline ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: c.muted,
                    fontWeight: 700,
                    lineHeight: 1,
                    display: { xs: "none", sm: "block" },
                    overflowWrap: "anywhere",
                  }}
                >
                  {brandTagline}
                </Typography>
              ) : null}
            </Box>
          </Stack>

          <Box sx={{ flex: 1 }} />

          {isCompactNav && showTopbarOrderButton ? (
            <Button
              component={RouterLink}
              to={ORDER_PATH}
              {...(!keepClientOrderInTab ? orderTargetProps : {})}
              aria-label={t("nav.order")}
              sx={orderButtonSx}
            >
              {t("nav.order")}
            </Button>
          ) : null}

          {/* Desktop nav */}
          {!isCompactNav ? (
            <Stack
              component="nav"
              aria-label={t("nav.primary")}
              direction="row"
              spacing={0.75}
              sx={{ alignItems: "center" }}
            >
              {mainNav.map((item) => {
                const active = isActive(item.to);

                return (
                  <Button
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    aria-current={active ? "page" : undefined}
                    sx={navButtonSx(active)}
                  >
                    {item.label}
                  </Button>
                );
              })}

              <Button
                id={MORE_BUTTON_ID}
                onClick={(e) => setMoreAnchorEl(e.currentTarget)}
                endIcon={<ExpandMoreRoundedIcon />}
                aria-controls={MORE_MENU_ID}
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                sx={navButtonSx(moreOpen)}
              >
                {t("nav.more")}
              </Button>

              <Menu
                anchorEl={moreAnchorEl}
                open={moreOpen}
                onClose={() => setMoreAnchorEl(null)}
                disableScrollLock
                MenuListProps={{
                  id: MORE_MENU_ID,
                  "aria-labelledby": MORE_BUTTON_ID,
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: "8px",
                    minWidth: 210,
                    border: "1px solid rgba(124,45,18,0.10)",
                    boxShadow: "0 20px 60px rgba(124,45,18,0.18)",
                    overflow: "hidden",
                  },
                }}
              >
                {moreNav.map((item) => (
                  <MenuItem
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    onClick={() => setMoreAnchorEl(null)}
                    selected={isActive(item.to)}
                    sx={{
                      gap: 1,
                      py: 1.15,
                      fontWeight: 800,
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>

              {languageButton}

              {clientPortalEnabled ? (
                clientPortalAuthenticated ? (
                  <Button
                    id={ACCOUNT_BUTTON_ID}
                    onClick={(event) => setAccountAnchorEl(event.currentTarget)}
                    startIcon={<AccountCircleRoundedIcon fontSize="small" />}
                    endIcon={<ExpandMoreRoundedIcon fontSize="small" />}
                    aria-controls={accountOpen ? ACCOUNT_MENU_ID : undefined}
                    aria-haspopup="menu"
                    aria-expanded={accountOpen}
                    aria-label={`${t("nav.account")}${
                      clientProfile?.displayName ? `: ${clientProfile.displayName}` : ""
                    }`}
                    sx={{ ...accountButtonSx, border: "1px solid rgba(124,45,18,0.12)" }}
                  >
                    {t("nav.account")}
                  </Button>
                ) : (
                  <Button
                    component={RouterLink}
                    to="/login"
                    aria-current={isActive("/login") ? "page" : undefined}
                    sx={accountButtonSx}
                  >
                    {t("nav.login")}
                  </Button>
                )
              ) : null}

              {showTopbarOrderButton ? (
                <Button
                  component={RouterLink}
                  to={ORDER_PATH}
                  {...(!keepClientOrderInTab ? orderTargetProps : {})}
                  aria-label={t("nav.order")}
                  sx={{ ...orderButtonSx, ml: 0.25 }}
                >
                  {t("nav.order")}
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={accountAnchorEl}
        open={clientPortalEnabled && clientPortalAuthenticated && !isCompactNav && accountOpen}
        onClose={() => setAccountAnchorEl(null)}
        disableScrollLock
        MenuListProps={{ id: ACCOUNT_MENU_ID, "aria-labelledby": ACCOUNT_BUTTON_ID }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ sx: {
          mt: 1, minWidth: 220, maxWidth: 280, borderRadius: "8px",
          border: "1px solid rgba(124,45,18,0.10)",
          boxShadow: "0 20px 60px rgba(124,45,18,0.18)",
          overflow: "hidden",
        } }}
      >
        <MenuItem component={RouterLink} to="/account"
          selected={isActive("/account")}
          aria-current={isActive("/account") ? "page" : undefined}
          onClick={() => setAccountAnchorEl(null)}
          sx={{ gap: 1, minHeight: 48, py: 1.15 }}>
          <AccountCircleRoundedIcon fontSize="small" />
          <ListItemText primary={t("nav.account")} secondary={clientProfile?.displayName || undefined}
            primaryTypographyProps={{ fontWeight: 700 }}
            secondaryTypographyProps={{ noWrap: true }} sx={{ minWidth: 0 }} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleClientLogout} sx={{ gap: 1, minHeight: 44, py: 1.15, fontWeight: 700 }}>
          <LogoutRoundedIcon fontSize="small" />
          {t("nav.logout")}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={languageAnchorEl}
        open={hasLanguages && languageOpen}
        onClose={closeLanguageMenu}
        disableScrollLock
        MenuListProps={{
          id: LANGUAGE_MENU_ID,
          "aria-labelledby": LANGUAGE_BUTTON_ID,
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "8px",
            minWidth: 190,
            border: "1px solid rgba(124,45,18,0.10)",
            boxShadow: "0 20px 60px rgba(124,45,18,0.18)",
            overflow: "hidden",
          },
        }}
      >
        {(languages || []).map((language) => {
          const active = String(language.iref) === String(selectedLanguageIref);
          const imageUrl = language.image ? toMediaUrl(language.image) : "";

          return (
            <MenuItem
              key={language.iref}
              onClick={() => chooseLanguage(language.iref)}
              selected={active}
              sx={{
                gap: 1,
                py: 1.05,
                fontWeight: active ? 950 : 800,
              }}
            >
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt=""
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "1px solid rgba(124,45,18,0.12)",
                  }}
                />
              ) : null}

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: "inherit", lineHeight: 1.15 }}>
                  {String(language.countryShortName || "").toUpperCase()}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {language.languageName || language.countryName || ""}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>

      <Box
        sx={{
          height: {
            xs: `${HEADER_H_XS}px`,
            sm: `${HEADER_H_SM}px`,
          },
          flexShrink: 0,
        }}
      />

      {!online ? (
        <Box
          role="status"
          sx={{
            bgcolor: c.dark,
            color: "#fff",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <Container maxWidth="xl" sx={{ py: 1 }}>
            <Alert
              icon={<WifiOffRoundedIcon fontSize="small" />}
              severity="warning"
              sx={{
                py: 0.25,
                bgcolor: "rgba(255,255,255,0.10)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.16)",
                "& .MuiAlert-icon": { color: c.primary },
              }}
            >
              <Typography component="span" sx={{ fontWeight: 850 }}>
                {t("common.offlineTitle")}.{" "}
              </Typography>
              {t("common.offline")}
            </Alert>
          </Container>
        </Box>
      ) : null}

      {online && publicContentError ? (
        <Box sx={{ bgcolor: c.background }}>
          <Container maxWidth="xl" sx={{ py: 1 }}>
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={retryPublicContent}
                  sx={{ minHeight: 36, fontWeight: 900 }}
                >
                  {t("common.retry")}
                </Button>
              }
              sx={{
                alignItems: "center",
                bgcolor: c.surface,
                color: c.text,
                border: "1px solid rgba(124,45,18,0.12)",
                "& .MuiAlert-icon": { color: c.accent },
              }}
            >
              {t("common.publicContentError")}
            </Alert>
          </Container>
        </Box>
      ) : null}

      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          id: MOBILE_NAV_ID,
          sx: {
            width: "min(340px, 88vw)",
            bgcolor: c.background,
            borderRight: "1px solid rgba(124,45,18,0.10)",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.75 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "8px",
                bgcolor: "rgba(255,255,255,0.8)",
                border: "1px solid rgba(124,45,18,0.10)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {logoUrl ? (
                <OptimizedConfigImage
                  src={logoUrl}
                  alt={logoAlt}
                  style={{ maxHeight: 40, maxWidth: 40, width: "auto", display: "block" }}
                />
              ) : (
                displayInitial ? (
                  <Typography sx={{ fontWeight: 950, color: c.accent }}>
                    {displayInitial}
                  </Typography>
                ) : (
                  <RestaurantMenuRoundedIcon
                    aria-hidden="true"
                    sx={{ color: c.accent }}
                  />
                )
              )}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 950,
                  color: c.text,
                  lineHeight: 1.1,
                  overflowWrap: "anywhere",
                }}
              >
                {brandLabel}
              </Typography>

              <Typography variant="body2" sx={{ color: c.muted }}>
                {t("nav.menu")}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 1.25, borderColor: "rgba(124,45,18,0.10)" }} />

          {hasLanguages ? (
            <>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.25 }}
              >
                <Typography variant="body2" sx={{ color: c.muted, fontWeight: 800 }}>
                  {t("language.change")}
                </Typography>
                {languageButton}
              </Stack>

              <Divider sx={{ mb: 1.25, borderColor: "rgba(124,45,18,0.10)" }} />
            </>
          ) : null}

          <Box component="nav" aria-label={t("nav.menu")}>
            <List dense disablePadding>
            {allNav.map((item) => {
              const active = isActive(item.to);

              return (
                <ListItemButton
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  selected={active}
                  aria-current={active ? "page" : undefined}
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    py: 1.1,
                    color: active ? c.accent : c.text,
                    bgcolor: active ? "rgba(255,237,213,0.88)" : "transparent",
                    "&.Mui-selected": {
                      bgcolor: "rgba(255,237,213,0.88)",
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: "#ffedd5",
                    },
                  }}
                >
                  <Box sx={{ mr: 1.25, display: "flex", color: "#c2410c" }}>
                    {item.icon}
                  </Box>

                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: active ? 950 : 800,
                    }}
                  />
                </ListItemButton>
              );
            })}

            {clientPortalEnabled ? (
              <>
                <Divider
                  component="li"
                  sx={{ my: 1, borderColor: "rgba(124,45,18,0.10)" }}
                />
                {clientPortalAuthenticated ? (
                  <>
                    <ListItemButton
                      component={RouterLink}
                      to="/account"
                      selected={isActive("/account")}
                      onClick={() => setDrawerOpen(false)}
                      aria-current={
                        isActive("/account") ? "page" : undefined
                      }
                      sx={{ borderRadius: "8px", mb: 0.5, py: 1.1 }}
                    >
                      <Box sx={{ mr: 1.25, display: "flex", color: "#c2410c" }}>
                        <AccountCircleRoundedIcon fontSize="small" />
                      </Box>
                      <ListItemText
                        primary={t("nav.account")}
                        secondary={
                          clientProfile?.displayName ||
                          t("portal.account.fallbackName")
                        }
                        primaryTypographyProps={{ fontWeight: 900 }}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItemButton>
                    <ListItemButton
                      onClick={handleClientLogout}
                      sx={{ borderRadius: "8px", mb: 0.5, py: 1.1 }}
                    >
                      <Box sx={{ mr: 1.25, display: "flex", color: "#c2410c" }}>
                        <LogoutRoundedIcon fontSize="small" />
                      </Box>
                      <ListItemText
                        primary={t("nav.logout")}
                        primaryTypographyProps={{ fontWeight: 900 }}
                      />
                    </ListItemButton>
                  </>
                ) : (
                  <ListItemButton
                    component={RouterLink}
                    to="/login"
                    selected={isActive("/login")}
                    onClick={() => setDrawerOpen(false)}
                    aria-current={isActive("/login") ? "page" : undefined}
                    sx={{ borderRadius: "8px", mb: 0.5, py: 1.1 }}
                  >
                    <Box sx={{ mr: 1.25, display: "flex", color: "#c2410c" }}>
                      <LoginRoundedIcon fontSize="small" />
                    </Box>
                    <ListItemText
                      primary={t("nav.login")}
                      primaryTypographyProps={{ fontWeight: 900 }}
                    />
                  </ListItemButton>
                )}
              </>
            ) : null}
            </List>
          </Box>
        </Box>
      </Drawer>

      {/* Content */}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{ flex: 1, outline: 0 }}
      >
        <Container
          maxWidth="xl"
          sx={{
            px: { xs: 2, sm: 3 },
            pt: isAppDownload ? 0 : { xs: 1.25, sm: 2, md: 2.5 },
            pb: {
              xs: isAppDownload ? 0 : 4,
              sm: isAppDownload ? 0 : 5,
              md: isAppDownload ? 0 : 6,
            },
          }}
        >
          {children}
        </Container>
      </Box>

      <Fab
        size="medium"
        aria-label={t("nav.backToTop")}
        onClick={scrollToTop}
        sx={{
          display: { xs: "none", sm: showTopAction ? "inline-flex" : "none" },
          position: "fixed",
          right: { sm: 24, lg: 34 },
          bottom: { sm: 28, lg: 34 },
          zIndex: 1180,
          width: 46,
          height: 46,
          borderRadius: "8px",
          color: c.onPrimary,
          bgcolor: c.primary,
          border: "1px solid rgba(17,17,17,0.08)",
          boxShadow: "0 16px 34px rgba(33,20,12,0.18)",
          "&:hover": {
            bgcolor: c.primary,
            transform: "translateY(-2px)",
          },
        }}
      >
        <KeyboardArrowUpRoundedIcon />
      </Fab>

      {/* Website footer */}
      <Box
        component="footer"
        sx={{
          mt: "auto",
          position: "static",
          borderTop: "3px solid",
          borderColor: c.primary,
          bgcolor: c.dark,
          color: "rgba(255,255,255,0.78)",
          py: { xs: 2.5, md: 3.25 },
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "minmax(0, 1fr) auto",
              },
              gap: { xs: 2, md: 3.5 },
              alignItems: "start",
            }}
          >
            <Stack spacing={1.25} sx={{ maxWidth: 640 }}>
              <Stack
                component={RouterLink}
                to="/"
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  alignSelf: "flex-start",
                  color: "#fff",
                  textDecoration: "none",
                }}
              >
                {logoUrl ? (
                  <Box
                    component={OptimizedConfigImage}
                    src={logoUrl}
                    alt={logoAlt}
                    sx={{
                      width: 40,
                      height: 40,
                      objectFit: "contain",
                      borderRadius: "8px",
                      bgcolor: "rgba(255,255,255,0.94)",
                      p: 0.5,
                    }}
                  />
                ) : null}

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#fff",
                      fontWeight: 950,
                      lineHeight: 1.1,
                      fontSize: { xs: 17, md: 18 },
                      overflowWrap: "anywhere",
                    }}
                  >
                    {brandLabel}
                  </Typography>
                  {brandTagline ? (
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 0.25,
                        color: "rgba(255,255,255,0.68)",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {brandTagline}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>

              {footerText ? (
                <Typography
                  component="p"
                  variant="body2"
                  sx={{
                    maxWidth: 580,
                    color: "rgba(255,255,255,0.76)",
                    lineHeight: 1.55,
                    whiteSpace: "pre-line",
                    overflowWrap: "anywhere",
                  }}
                >
                  {footerText}
                </Typography>
              ) : null}

              {socialLinks.length ? (
                <Stack direction="row" spacing={0.75}>
                  {socialLinks.map((item) => (
                    <IconButton
                      key={item.label}
                      size="small"
                      component="a"
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.label}
                      sx={{
                        color: "#fff",
                        bgcolor: "rgba(255,255,255,0.10)",
                        border: "1px solid rgba(255,255,255,0.16)",
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.18)",
                        },
                      }}
                    >
                      {item.icon}
                    </IconButton>
                  ))}
                </Stack>
              ) : null}
            </Stack>

            <Box
              component="nav"
              aria-label={t("nav.primary")}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
              }}
            >
              {footerNav.map((item) => (
                <Button
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  color="inherit"
                  size="small"
                  sx={{
                    minWidth: 0,
                    justifyContent: "flex-start",
                    px: 0.75,
                    color: "rgba(255,255,255,0.82)",
                    textTransform: "none",
                    fontWeight: 800,
                    "&:hover": {
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Divider
            sx={{
              my: { xs: 2, md: 2.5 },
              borderColor: "rgba(255,255,255,0.14)",
            }}
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.5, md: 2 }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.58)",
                overflowWrap: "anywhere",
              }}
            >
              © {new Date().getFullYear()} {brandLabel}
            </Typography>

            <Box
              component="a"
              href="https://reclamatiisal.anpc.ro/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: "block", width: 250, maxWidth: "100%", flexShrink: 0 }}
            >
              {/* Official asset: https://www.anpc.ro/download/sal/SAL-PICTOGRAMA.png */}
              <Box
                component="img"
                src={`${(process.env.PUBLIC_URL || "").replace(/\/+$/, "")}/assets/anpc-sal.png`}
                alt="Soluționarea Alternativă a Litigiilor - ANPC"
                width={250}
                height={50}
                sx={{
                  display: "block",
                  maxWidth: "100%",
                  objectFit: "scale-down",
                  objectPosition: "left center",
                }}
              />
            </Box>

            <Stack
              direction="row"
              spacing={0.5}
              sx={{ flexWrap: "wrap" }}
            >
              <Button
                component={RouterLink}
                to="/terms"
                color="inherit"
                size="small"
                sx={{
                  color: "rgba(255,255,255,0.70)",
                  textTransform: "none",
                  fontWeight: 750,
                }}
              >
                {t("skipline.terms")}
              </Button>
              <Button
                component={RouterLink}
                to="/privacy"
                color="inherit"
                size="small"
                sx={{
                  color: "rgba(255,255,255,0.70)",
                  textTransform: "none",
                  fontWeight: 750,
                }}
              >
                {t("skipline.privacy")}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
